import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { startSession, refreshSession, useSession } from './lib/session';
import SessionRecovery from './components/SessionRecovery';
import CreateAccountPage from './pages/CreateAccountPage';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import UploadPage from './pages/UploadPage';
import LoadingPage from './pages/LoadingPage';
import ResultsPage from './pages/ResultsPage';
import HistoryPage from './pages/HistoryPage';
import ProjectPage from './pages/ProjectPage';
import ConsultantWorkspacePage from './pages/ConsultantWorkspacePage';
import PartnerDashboardPage from './pages/PartnerDashboardPage';
import TemplateLibraryPage from './pages/TemplateLibraryPage';
import Layout from './components/Layout';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { useAuth0 } from '@auth0/auth0-react';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const session = useSession();
  const expected = isAuthenticated ? user?.sub : 'anonymous';
  if (
    isLoading ||
    session.identity !== expected ||
    session.status === 'idle' ||
    session.status === 'loading'
  )
    return <SessionRecovery loading />;
  if (session.status !== 'ready') return <SessionRecovery />;
  return <>{children}</>;
}
function AuthBootstrap() {
  const { isAuthenticated, isLoading, user, error } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const identity = isAuthenticated ? user?.sub : 'anonymous';
  const redirected = useRef<string | null>(null);
  useEffect(() => {
    if (
      isLoading ||
      (error && isAuthenticated) ||
      !identity ||
      location.pathname === '/auth/callback'
    )
      return;
    let active = true;
    void startSession(identity)
      .then(({ claimed }) => {
        if (active && claimed && redirected.current !== identity) {
          redirected.current = identity;
          navigate('/history', { replace: true });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [
    identity,
    isAuthenticated,
    isLoading,
    error,
    location.pathname,
    navigate,
  ]);
  useEffect(() => {
    const refresh = () => {
      void refreshSession().catch(() => {});
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);
  return null;
}

export function AppRoutes() {
  const { t } = useTranslation();
  const location = useLocation();
  const conversionPath = useSession(state => state.state?.conversionPath);
  const resetKey = location.key || location.pathname;

  const createErrorContent = (
    page: 'upload' | 'loading' | 'results' | 'history'
  ) => ({
    eyebrow: t('routeErrors.eyebrow'),
    title: t(`routeErrors.pages.${page}.title`),
    message: t(`routeErrors.pages.${page}.message`),
    retryLabel: t('routeErrors.actions.retry'),
    backLabel: t('routeErrors.actions.goBack'),
    primaryLabel:
      page === 'results' || page === 'history'
        ? t('routeErrors.actions.goHistory')
        : t('routeErrors.actions.newScan'),
    primaryHref:
      page === 'results' || page === 'history' ? '/history' : '/scan',
  });

  return (
    <>
      <AuthBootstrap />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/create-account" element={<CreateAccountPage />} />
        {conversionPath && conversionPath !== '/create-account' && (
          <Route path={conversionPath} element={<CreateAccountPage />} />
        )}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route path="/" element={<Navigate to="/scan" replace />} />
          <Route
            path="/scan"
            element={
              <RouteErrorBoundary
                page="upload"
                resetKey={resetKey}
                content={createErrorContent('upload')}
              >
                <UploadPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/scan/:id/processing"
            element={
              <RouteErrorBoundary
                page="loading"
                resetKey={resetKey}
                content={createErrorContent('loading')}
              >
                <LoadingPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/scan/:id/results"
            element={
              <RouteErrorBoundary
                page="results"
                resetKey={resetKey}
                content={createErrorContent('results')}
              >
                <ResultsPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/history"
            element={
              <RouteErrorBoundary
                page="history"
                resetKey={resetKey}
                content={createErrorContent('history')}
              >
                <HistoryPage />
              </RouteErrorBoundary>
            }
          />
          <Route path="/partner" element={<PartnerDashboardPage />} />
          <Route path="/templates" element={<TemplateLibraryPage />} />
          <Route
            path="/projects/:projectId"
            element={
              <RouteErrorBoundary
                page="history"
                resetKey={resetKey}
                content={createErrorContent('history')}
              >
                <ProjectPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/projects/:projectId/consultant"
            element={
              <RouteErrorBoundary
                page="history"
                resetKey={resetKey}
                content={createErrorContent('history')}
              >
                <ConsultantWorkspacePage />
              </RouteErrorBoundary>
            }
          />
        </Route>

        <Route
          path="*"
          element={
            <RequireAuth>
              <Navigate to="/" replace />
            </RequireAuth>
          }
        />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <AppRoutes />
    </BrowserRouter>
  );
}
